import { Button } from "@tcg/ui";
import { MonitorSmartphone } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { LanguagePicker } from "@/components/LanguagePicker";
import type { UnpairedNotice } from "@/lib/auth";
import { errorText, isApiError } from "@/lib/errors";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CODE = /^[0-9]{6,12}$/;

export interface PairingLink {
  /** The register the admin's QR was made for ("" when opened without it). */
  registerId: string;
  /** The one-use pairing code from the QR ("" when typed by hand). */
  code: string;
}

/** What the admin's pairing link / QR carries (`/pair?register=<uuid>&code=<8 digits>`). */
export function pairingFromUrl(
  search: string = typeof window === "undefined" ? "" : window.location.search,
): PairingLink {
  const params = new URLSearchParams(search);
  const registerId = params.get("register") ?? "";
  const code = (params.get("code") ?? "").replace(/\D/g, "");
  return {
    registerId: UUID.test(registerId) ? registerId : "",
    code: CODE.test(code) ? code : "",
  };
}

/** The register id from the admin's pairing link (kept for callers of T07-22). */
export function registerFromUrl(search?: string): string {
  return pairingFromUrl(search).registerId;
}

/**
 * 「端末の登録」 — pair this tablet with a register (T07-22, identity T01-05).
 *
 * Staff type only the 8-digit code the admin shows: identity knows which register the code was
 * made for (`POST /pos/auth/pair`), so nobody reads out a register id. Opened from the admin's
 * QR, the code is filled in and the query string is cleared at once (the code must not stay in
 * the address bar, history or an access log if pairing fails).
 */
export function PairScreen({ notice }: { notice: UnpairedNotice }) {
  const { t } = useTranslation();
  const { auth } = usePos();
  const [link] = useState(() => pairingFromUrl());
  const [code, setCode] = useState(link.code);
  // the suggested name follows the language picker until someone types their own
  const [typedName, setName] = useState<string | null>(null);
  const name = typedName ?? t("pair.device_name_default");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const valid = CODE.test(code) && name.trim().length > 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await auth.pair({
        // opened from the admin's link: that register; typed by hand: the code names it
        registerId: link.registerId || null,
        pairingCode: code,
        deviceName: name.trim(),
      });
    } catch (err) {
      if (isApiError(err, "rate_limited")) {
        const seconds = Number(err.detail.retryAfter ?? 600);
        setError(
          t("errors.rate_limited_pairing", { minutes: Math.max(1, Math.ceil(seconds / 60)) }),
        );
      } else {
        setError(errorText(err, t));
      }
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-full items-center justify-center bg-muted p-6">
      <form
        onSubmit={(e) => void submit(e)}
        className="flex w-full max-w-xl flex-col gap-5 rounded-xl border bg-background p-8 shadow"
      >
        <div className="flex flex-wrap items-center gap-3">
          <MonitorSmartphone aria-hidden className="size-10" />
          <h1 className="text-3xl font-semibold">{t("pair.title")}</h1>
          <LanguagePicker className="ml-auto" />
        </div>
        {notice ? (
          <p
            role="alert"
            className="rounded-md border border-status-problem bg-status-problem/10 p-3 text-lg"
          >
            {t(notice === "revoked" ? "pair.revoked" : "pair.expired")}
          </p>
        ) : null}
        <p className="text-lg text-muted-foreground">
          {link.code ? t("pair.code_from_link") : t("pair.intro")}
        </p>
        <label className="flex flex-col gap-1">
          <span className="font-medium">{t("pair.code_label")}</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 12))}
            inputMode="numeric"
            autoComplete="one-time-code"
            className="min-h-pos-touch rounded-md border px-3 text-center font-mono text-3xl tracking-[0.4em]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-medium">{t("pair.device_name_label")}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            className="min-h-pos-touch rounded-md border px-3 text-lg"
          />
        </label>
        {error ? (
          <p role="alert" className="text-lg text-pos-problem">
            {error}
          </p>
        ) : null}
        <Button type="submit" size="pos" disabled={!valid || busy}>
          {busy ? t("app.saving") : t("pair.submit")}
        </Button>
      </form>
    </main>
  );
}
